import React, { useMemo, useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { DataTable, Column } from '../shared/DataTable';
import { MasteryBar } from '../shared/MasteryBar';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { CheckCircle2, XCircle, Search, Filter, BookOpen, FileText, X, Lightbulb, AlertTriangle } from 'lucide-react';
import { TAXONOMY, QUESTIONS_BY_FAMILY } from '../../../content';
import { resolveUnifiedSupport, FAMILY_SUPPORT_REGISTRY, type ResolvedSupport, type HintStep } from '../../../support';
interface FamilySupportRow {
  id: string;
  familyId: string;
  module: string;
  tier: number;
  questionCount: number;
  support: ResolvedSupport;
  completeness: number;
  hintCount: number;
  registryHints: HintStep[];
  inRegistry: boolean;
}
export function SupportContentPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'full' | 'partial' | 'none'>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'enriched' | 'sparse' | 'none'>('all');
  const [selectedFamily, setSelectedFamily] = useState<FamilySupportRow | null>(null);
  const allData: FamilySupportRow[] = useMemo(() => {
    return TAXONOMY.map(family => {
      const support = resolveUnifiedSupport(family.familyId);
      const registryEntry = FAMILY_SUPPORT_REGISTRY[family.familyId];
      const registryHints = registryEntry?.hints || [];
      return {
        id: family.familyId,
        familyId: family.familyId,
        module: family.module,
        tier: family.tier,
        questionCount: QUESTIONS_BY_FAMILY.get(family.familyId)?.length ?? 0,
        support,
        completeness: support.coverage.completeness,
        hintCount: registryHints.length,
        registryHints,
        inRegistry: !!registryEntry
      };
    });
  }, []);
  const stats = useMemo(() => {
    const total = allData.length;
    const full = allData.filter(d => d.completeness === 100).length;
    const partial = allData.filter(d => d.completeness > 0 && d.completeness < 100).length;
    const none = allData.filter(d => d.completeness === 0).length;
    const inRegistry = allData.filter(d => d.inRegistry).length;
    const enrichedHints = allData.filter(d => d.hintCount >= 2).length;
    const sparseHints = allData.filter(d => d.hintCount === 1).length;
    const noHints = allData.filter(d => d.hintCount === 0).length;
    const totalHints = allData.reduce((sum, d) => sum + d.hintCount, 0);
    const overallCoverage = Math.round(inRegistry / total * 100);
    return {
      total,
      full,
      partial,
      none,
      inRegistry,
      enrichedHints,
      sparseHints,
      noHints,
      totalHints,
      overallCoverage
    };
  }, [allData]);
  const moduleStats = useMemo(() => {
    const modules = Array.from(new Set(allData.map(d => d.module))).sort();
    return modules.map(mod => {
      const moduleData = allData.filter(d => d.module === mod);
      const familyCount = moduleData.length;
      const avgCompleteness = Math.round(moduleData.reduce((sum, d) => sum + d.completeness, 0) / familyCount);
      const totalHints = moduleData.reduce((sum, d) => sum + d.hintCount, 0);
      return {
        name: mod,
        familyCount,
        avgCompleteness,
        totalHints
      };
    });
  }, [allData]);
  const filteredData = useMemo(() => {
    return allData.filter(row => {
      if (searchQuery && !row.familyId.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (moduleFilter !== 'all' && row.module !== moduleFilter) return false;
      if (coverageFilter === 'full' && row.completeness !== 100) return false;
      if (coverageFilter === 'partial' && (row.completeness === 0 || row.completeness === 100)) return false;
      if (coverageFilter === 'none' && row.completeness !== 0) return false;
      if (hintFilter === 'enriched' && row.hintCount < 2) return false;
      if (hintFilter === 'sparse' && row.hintCount !== 1) return false;
      if (hintFilter === 'none' && row.hintCount !== 0) return false;
      return true;
    });
  }, [allData, searchQuery, moduleFilter, coverageFilter, hintFilter]);
  const modules = useMemo(() => Array.from(new Set(TAXONOMY.map(f => f.module))).sort(), []);
  const exampleQuestion = useMemo(() => {
    if (!selectedFamily) return null;
    const questions = QUESTIONS_BY_FAMILY.get(selectedFamily.familyId);
    return questions && questions.length > 0 ? questions[0] : null;
  }, [selectedFamily]);
  const renderCheck = (hasItem: boolean) => {
    return hasItem ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4032" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-4033" />;
  };
  const renderHintCount = (count: number) => {
    if (count === 0) {
      return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-4034">
          <XCircle className="w-3.5 h-3.5" data-id="element-4035" />
          <span data-id="element-4036">0</span>
        </span>;
    }
    if (count === 1) {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200" data-id="element-4037">
          <AlertTriangle className="w-3 h-3" data-id="element-4038" />
          <span data-id="element-4039">1</span>
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200" data-id="element-4040">
        <Lightbulb className="w-3 h-3" data-id="element-4041" />
        <span data-id="element-4042">{count}</span>
      </span>;
  };
  const columns: Column<FamilySupportRow>[] = [{
    key: 'familyId',
    label: 'Family ID',
    render: val => <span className="font-mono text-xs font-medium text-gray-700" data-id="element-4043">
          {val}
        </span>
  }, {
    key: 'module',
    label: 'Domein',
    render: val => <Badge variant="secondary" data-id="element-4044">{val}</Badge>
  }, {
    key: 'tier',
    label: 'Tier',
    render: val => <Badge variant="secondary" data-id="element-4045">T{val}</Badge>
  }, {
    key: 'questionCount',
    label: 'Vragen',
    render: val => <span className="text-xs font-medium text-gray-600" data-id="element-4046">{val}</span>
  }, {
    key: 'explanation',
    label: 'Uitleg',
    render: (_, row) => renderCheck(row.support.coverage.hasExplanation)
  }, {
    key: 'cheatSheet',
    label: 'Spiekbriefje',
    render: (_, row) => renderCheck(row.support.coverage.hasCheatSheet)
  }, {
    key: 'hintCount',
    label: 'Hints',
    render: val => renderHintCount(val)
  }, {
    key: 'steps',
    label: 'Stappen',
    render: (_, row) => renderCheck(row.support.coverage.hasSteps)
  }, {
    key: 'completeness',
    label: 'Compleetheid',
    render: val => <MasteryBar value={val} data-id="element-4047" />
  }];
  // Helper for module colors
  const getModuleColor = (index: number) => {
    const colors = ['border-blue-500', 'border-purple-500', 'border-emerald-500', 'border-amber-500', 'border-rose-500', 'border-cyan-500', 'border-indigo-500', 'border-orange-500', 'border-teal-500', 'border-pink-500'];
    return colors[index % colors.length];
  };
  return <PageLayout title="Ondersteuning Content" subtitle="Uitleg, hints en spiekbriefjes per family — compleet overzicht van alle ondersteuningscontent." actions={<ConnectionBadge status="live" data-id="element-4049" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Ondersteuning'
  }]} data-id="element-4048">
      {/* Module Summary Cards */}
      <div className="mb-6" data-id="element-4050">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4051">
          Status per Domein
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-4052">
          {moduleStats.map((mod, idx) => <Card key={mod.name} className={`p-3 border-l-4 ${getModuleColor(idx)}`} data-id="element-4053">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4054">
                {mod.name}
              </div>
              <div className="flex items-end justify-between" data-id="element-4055">
                <div data-id="element-4056">
                  <div className="text-lg font-bold text-gray-900" data-id="element-4057">
                    {mod.avgCompleteness}%
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4058">Compleet</div>
                </div>
                <div className="text-right" data-id="element-4059">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4060">
                    {mod.familyCount} fam
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4061">
                    {mod.totalHints} hints
                  </div>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4 mb-6" data-id="element-4062">
        <StatCard label="Coverage" value={`${stats.overallCoverage}%`} icon={<BookOpen className="w-5 h-5" data-id="element-4064" />} color={stats.overallCoverage === 100 ? 'emerald' : stats.overallCoverage >= 80 ? 'amber' : 'blue'} data-id="element-4063" />
        <StatCard label="In Registry" value={stats.inRegistry} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4066" />} color="emerald" data-id="element-4065" />
        <StatCard label="Totaal Hints" value={stats.totalHints} icon={<Lightbulb className="w-5 h-5" data-id="element-4068" />} color="blue" data-id="element-4067" />
        <StatCard label="Verrijkt (≥2 hints)" value={stats.enrichedHints} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4070" />} color="emerald" data-id="element-4069" />
        <StatCard label="Schaars (1 hint)" value={stats.sparseHints} icon={<AlertTriangle className="w-5 h-5" data-id="element-4072" />} color="amber" data-id="element-4071" />
        <StatCard label="Geen Hints" value={stats.noHints} icon={<XCircle className="w-5 h-5" data-id="element-4074" />} color="red" data-id="element-4073" />
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-4075">
        <span className="text-xs font-medium text-gray-500 mr-1" data-id="element-4076">
          Snelfilters:
        </span>
        <button onClick={() => {
        setCoverageFilter('none');
        setHintFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'none' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-4077">
          Ontbrekend
        </button>
        <button onClick={() => {
        setCoverageFilter('partial');
        setHintFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'partial' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-4078">
          Onvolledig
        </button>
        <button onClick={() => {
        setHintFilter('none');
        setCoverageFilter('all');
        setModuleFilter('all');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${hintFilter === 'none' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-4079">
          Geen hints
        </button>
        <button onClick={() => {
        setCoverageFilter('all');
        setHintFilter('all');
        setModuleFilter('all');
        setSearchQuery('');
      }} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${coverageFilter === 'all' && hintFilter === 'all' && moduleFilter === 'all' && searchQuery === '' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'}`} data-id="element-4080">
          Alles
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-4081">
        <div className="flex-1" data-id="element-4082">
          <Input type="text" placeholder="Zoek op Family ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4084" />} variant="admin" size="sm" data-id="element-4083" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-4085">
          <div className="hidden sm:block" data-id="element-4086">
            <Filter className="w-4 h-4 text-gray-400" data-id="element-4087" />
          </div>
          <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
          value: 'all',
          label: 'Alle domeinen'
        }, ...modules.map(m => ({
          value: m,
          label: m
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4088" />
          <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value as typeof coverageFilter)} options={[{
          value: 'all',
          label: 'Alle dekkingen'
        }, {
          value: 'full',
          label: 'Volledig (100%)'
        }, {
          value: 'partial',
          label: 'Gedeeltelijk (1-99%)'
        }, {
          value: 'none',
          label: 'Geen (0%)'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-4089" />
          <Select value={hintFilter} onChange={e => setHintFilter(e.target.value as typeof hintFilter)} options={[{
          value: 'all',
          label: 'Alle hints'
        }, {
          value: 'enriched',
          label: 'Verrijkt (≥2)'
        }, {
          value: 'sparse',
          label: 'Schaars (1)'
        }, {
          value: 'none',
          label: 'Geen hints'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-4090" />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-4091">
        {/* Table */}
        <div className={`flex-1 transition-all ${selectedFamily ? 'lg:w-2/3' : 'w-full'}`} data-id="element-4092">
          <DataTable columns={columns} data={filteredData} onRowClick={setSelectedFamily} emptyMessage="Geen families gevonden met deze filters." data-id="element-4093" />
        </div>

        {/* Preview Panel */}
        {selectedFamily && <div className="w-full lg:w-1/3 flex-shrink-0" data-id="element-4094">
            <Card className="sticky top-6 flex flex-col max-h-[calc(100vh-120px)] shadow-sm" data-id="element-4095">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 rounded-t-xl" data-id="element-4096">
                <div data-id="element-4097">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-4098">
                    <FileText className="w-4 h-4 text-blue-500" data-id="element-4099" />
                    Content Preview
                  </h3>
                  <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-4100">
                    {selectedFamily.familyId}
                  </p>
                </div>
                <button onClick={() => setSelectedFamily(null)} className="p-1.5 h-auto text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors" data-id="element-4101">
                  <X className="w-4 h-4" data-id="element-4102" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto flex-1 space-y-6" data-id="element-4103">
                {/* Registry status */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${selectedFamily.inRegistry ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-4104">
                  {selectedFamily.inRegistry ? <>
                      <CheckCircle2 className="w-4 h-4" data-id="element-4105" /> In
                      FAMILY_SUPPORT_REGISTRY
                    </> : <>
                      <XCircle className="w-4 h-4" data-id="element-4106" /> Niet in registry — geen
                      support content
                    </>}
                </div>

                {/* Family Context */}
                <div data-id="element-4107">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-4108">
                    Family Context
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-4109">
                    <Badge variant="secondary" data-id="element-4110">
                      Tier {selectedFamily.tier}
                    </Badge>
                    <Badge variant="secondary" data-id="element-4111">
                      {selectedFamily.questionCount} Vragen
                    </Badge>
                    <Badge variant="secondary" data-id="element-4112">{selectedFamily.module}</Badge>
                  </div>
                </div>

                {/* Example Question */}
                {exampleQuestion && <div data-id="element-4113">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-4114">
                      Voorbeeld Vraag
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-4115">
                      <p className="text-sm text-gray-800 leading-relaxed mb-2" data-id="element-4116">
                        {exampleQuestion.question}
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-id="element-4117">
                        <Badge variant="secondary" data-id="element-4118">
                          {exampleQuestion.questionType}
                        </Badge>
                        <Badge variant="secondary" data-id="element-4119">
                          {exampleQuestion.levelCode}
                        </Badge>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-4120">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-4121">
                    {renderCheck(selectedFamily.support.coverage.hasExplanation)}{' '}
                    Uitleg (Zo werkt het)
                  </h4>
                  {selectedFamily.support.explanation ? <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100" data-id="element-4122">
                      <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-4123">
                        {selectedFamily.support.explanation.title}
                      </p>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap" data-id="element-4124">
                        {selectedFamily.support.explanation.body}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-4125">
                      Geen uitleg beschikbaar.
                    </p>}
                </div>

                <div data-id="element-4126">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-4127">
                    {renderCheck(selectedFamily.support.coverage.hasSteps)}{' '}
                    Stappenplan
                  </h4>
                  {selectedFamily.support.steps.length > 0 ? <ol className="list-decimal list-inside space-y-1" data-id="element-4128">
                      {selectedFamily.support.steps.map((step, i) => <li key={i} className="text-sm text-gray-700" data-id="element-4129">
                          {step}
                        </li>)}
                    </ol> : <p className="text-sm text-gray-400 italic" data-id="element-4130">
                      Geen stappenplan beschikbaar.
                    </p>}
                </div>

                <div data-id="element-4131">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-4132">
                    {renderHintCount(selectedFamily.hintCount)} Hints (
                    {selectedFamily.hintCount})
                  </h4>
                  {selectedFamily.registryHints.length > 0 ? <div className="space-y-2" data-id="element-4133">
                      {selectedFamily.registryHints.map((hint, i) => <div key={i} className="bg-amber-50 p-3 rounded-lg border border-amber-100" data-id="element-4134">
                          {hint.title && <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1" data-id="element-4135">
                              {hint.title}
                            </p>}
                          <p className="text-sm text-amber-900 whitespace-pre-wrap" data-id="element-4136">
                            {hint.content}
                          </p>
                          {hint.visual && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-id="element-4137">
                              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" data-id="element-4138" />
                              Visueel: {hint.visual.type}
                            </p>}
                        </div>)}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-4139">
                      Geen hints beschikbaar.
                    </p>}
                </div>

                <div data-id="element-4140">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-4141">
                    {renderCheck(selectedFamily.support.coverage.hasCheatSheet)}{' '}
                    Spiekbriefje
                  </h4>
                  {selectedFamily.support.cheatSheet ? <div className="bg-emerald-50/50 p-3 rounded-lg border border-emerald-100" data-id="element-4142">
                      <p className="text-sm font-medium text-emerald-900 mb-1" data-id="element-4143">
                        {selectedFamily.support.cheatSheet.title}
                      </p>
                      <p className="text-sm text-emerald-800 whitespace-pre-wrap" data-id="element-4144">
                        {selectedFamily.support.cheatSheet.body}
                      </p>
                      {selectedFamily.support.cheatSheet.rules && selectedFamily.support.cheatSheet.rules.length > 0 && <ul className="list-disc list-inside mt-2 text-sm text-emerald-800" data-id="element-4145">
                            {selectedFamily.support.cheatSheet.rules.map((rule, i) => <li key={i} data-id="element-4146">{rule}</li>)}
                          </ul>}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-4147">
                      Geen spiekbriefje beschikbaar.
                    </p>}
                </div>

                <div data-id="element-4148">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-4149">
                    {renderCheck(selectedFamily.support.coverage.hasAfterSummary)}{' '}
                    Sessie Samenvatting
                  </h4>
                  {selectedFamily.support.afterSummary ? <div className="bg-purple-50 p-3 rounded-lg border border-purple-100" data-id="element-4150">
                      <p className="text-sm text-purple-900" data-id="element-4151">
                        {selectedFamily.support.afterSummary}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-4152">
                      Geen samenvatting beschikbaar.
                    </p>}
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </PageLayout>;
}